import { Center, VerticalCenter } from "@/common/css";
import CustomLink from "@/common/ui/CustomLink";
import BrokenLink from "@/common/ui/error/BrokenLink";
import { Image } from "@/common/ui/Image";
import { RoundLoader } from "@/common/ui/Loader";
import { H2, H3, LightText2 } from "@/common/ui/Text";
import FormRender from "@/components/Form/Renderer/RenderForm";
import { Button } from "@/components/ui/button";
import ShareLayout from "@/layouts/share.layout";
import { checkoutEmployeeReport, useShareDocInfo, useSharePaymentValidate } from "@/services/employee.services";
import { FILE_EXTENSION_ICON_MAP } from "@/utils/helper/fileUploadValidation";
import { toastErrorMessage, toastMessage } from "@/utils/helper/myToast";
import { emailRegex } from "@/utils/helper/string";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";

const CheckoutConfig = {
  submitBtnText: "Download",
  showLabel: true,
  sections: [
    {
      widgets: [
        {
          widgetType: "text",
          label: "Name",
          field: "name",
          apiField: "name",
          validation: {
            required: true,
            minLength: 1,
            maxLength: 35,
          },
        },
        {
          widgetType: "text",
          label: "Business email",
          field: "email",
          apiField: "email",
          validation: {
            required: true,
            pattern: {
              value: emailRegex,
              message: "Enter a valid email address.",
            },
          },
        },
        {
          widgetType: "radio",
          label: "Would you like to receive sales call for lystproof?",
          field: "isAgreeToSales",
          options: [
            { value: true, label: "Yes" },
            { value: false, label: "No" },
          ],
          defaultValue: "Yes",
        },
      ],
    },
  ],
};

const ShareDocument: React.FC = () => {
  const { shareId } = useParams();
  const [searchParams] = useSearchParams();
  const hostedPageId = searchParams.get("hostedpage_id");
  const { data, isLoading, error }: any = useShareDocInfo({ share_id: shareId, hostedPageId });
  const location = useLocation();
  const navigate = useNavigate();

  if (data) {
    CheckoutConfig.submitBtnText = `Proceed to Payment - ${data?.currency} ${data?.price}`;
  }

  const handleCheckout = async (data: any) => {
    try {
      const res = await checkoutEmployeeReport({ ...data, shareId });
      if (res?.status === 200) {
        const paymentUrl = res.data?.data?.payment_url;
        window.location.href = paymentUrl;
      } else {
        console.error("Checkout failed", res);
        const { message, isError, showMessage } = res?.response?.data || {};
        if (message) {
          return showMessage && toastMessage(message, isError);
        }
        if (res.message) {
          return toastMessage(res.message, true);
        }
      }
    } catch (error) {
      console.error("Checkout error:", error);
    }
  };
  const handleCloseModal = () => {
    navigate(location.pathname);
  };

  return (
    <ShareLayout>
      {isLoading ? (
        <RoundLoader />
      ) : (
        <div className="w-full h-full bg-brand-white2 flex flex-col items-center gap-10 relative">
          <div className="md:w-[889px] pt-14 md:pt-10">
            <div className="px-3 py-4 md:p-6 flex flex-col md:flex-row items-center justify-between bg-white rounded-20">
              <H3 text={"Start manage your employee on lystproof for free"} />
              <CustomLink link={{ value: "/", text: "Sign up for free" }} variant="header" className="min-w-72" />
            </div>
          </div>
          {error?.response?.data?.message ? (
            <BrokenLink message={error?.response?.data?.message} />
          ) : (
            <div className="flex flex-col md:flex-row justify-between w-full md:w-[889px] gap-4 pb-10 md:pb-0 md:gap-10">
              <div className="flex flex-col items-center justify-center p-4 py-6 md:py-8 bg-white w-full md:w-2/5 gap-4 rounded-20">
                <Image src={FILE_EXTENSION_ICON_MAP["pdf"]} alt="" className="size-16 md:size-24" />
                <H2 text={`${data?.info?.empName} - Background verification report.`} className="text-center text-sm md:text-base" />
                <div className="flex w-full mt-2 md:mt-4 gap-2 items-center justify-center md:justify-start">
                  <LightText2 text="Shared by " className="!text-text-light2 text-xs md:text-sm" />
                  <Image src={data?.info?.cmpLogo} alt="Company Logo" className="w-full max-w-32 md:max-w-56 h-auto object-contain block" />
                </div>
                <div className="flex w-full gap-2 items-center justify-center md:justify-start">
                  <LightText2 text="Format" className="!text-text-light2 text-xs md:text-sm" />
                  <LightText2 className="capitalize text-xs md:text-sm" text={`.${data?.info?.docFormate}`} />
                </div>
              </div>
              <FormRender
                handleFormSubmit={handleCheckout}
                formConfig={CheckoutConfig}
                className="p-4 py-6 md:py-8 bg-white rounded-20 w-full md:w-1/2"
                formClassName=""
                btnContainerClass="!pt-1"
                useFormConfig={{ mode: "onSubmit", reValidateMode: "onChange", criteriaMode: "all" }}
                disabledWarning
              />
            </div>
          )}

          {hostedPageId && shareId && <PaymentValidationModal shareId={shareId} hostedPageId={hostedPageId} handleCloseModal={handleCloseModal} />}
        </div>
      )}
    </ShareLayout>
  );
};

export default ShareDocument;

interface ModalProps {
  hostedPageId: string;
  shareId: string;
  handleCloseModal: () => void;
}

const PaymentValidationModal: React.FC<ModalProps> = ({ hostedPageId, shareId }) => {
  const { data, isLoading, error }: any = useSharePaymentValidate({ share_id: shareId, hostedpage_id: hostedPageId });
  const [isDownloading, setIsDownloading] = useState(false);
  const [hasDownloaded, setHasDownloaded] = useState(false);

  const DownloadDoc = async (data: any) => {
    setIsDownloading(true);
    try {
      const response = await fetch(data.download_link);
      if (response?.status !== 200) {
        return toastErrorMessage("Something went wrong!");
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "Employee Report";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download failed:", error);
    } finally {
      setIsDownloading(false);
      setHasDownloaded(true);
    }
  };

  useEffect(() => {
    if (data?.download_link && !hasDownloaded) {
      DownloadDoc(data);
    }
  }, [data?.download_link, hasDownloaded]);

  return (
    <div className={`absolute top-0 z-50 h-full right-0 flex justify-center items-center w-full`}>
      <div className="flex flex-col items-center justify-center bg-white w-full h-[calc(100%-8px)] relative px-4">
        {isLoading ? (
          <div className="flex flex-col gap-1 w-full items-center">
            <RoundLoader />
            <LightText2 text="Validating Payment" className="py-2 text-center" />
          </div>
        ) : (
          <div className="flex items-center flex-col justify-center p-4 md:p-12 py-1">
            <VerticalCenter className="pt-4">
              {error?.response?.data?.message && <LightText2 text={error?.response?.data?.message} className="text-red text-center" />}
              {data?.status === "success" && (
                <>
                  <video 
                    src="https://lp-asset.gigglly.com/asset/icons/Done.mp4" 
                    autoPlay 
                    loop 
                    muted 
                    playsInline 
                    preload="auto" 
                    className="w-16 h-16 md:w-auto md:h-auto"
                  />
                  <H2 text="Payment complete!" className="!text-13 text-center" />
                </>
              )}
            </VerticalCenter>
            {data?.download_link && (
              <Center>
                <LightText2 text="Your file will start downloading automatically. If it doesn’t, please" className="" />
                <Button variant="link" disabled={isDownloading} className="text-13 text-text-primary !px-1 font-bold" onClick={() => DownloadDoc(data)}>
                  click here.
                </Button>
              </Center>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
